import { useEffect } from "react";
import type { LandingContent, Locale } from "../content/types";
import { localeHref } from "../i18n/routes";
import { Icon } from "./Icon";

type MobileMenuProps = {
  locale: Locale;
  nav: LandingContent["nav"];
  open: boolean;
  onClose: () => void;
};

export function MobileMenu({ locale, nav, open, onClose }: MobileMenuProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const landingHref = localeHref(locale, "landing");
  const links = [
    { label: nav.features, href: `${landingHref}#features` },
    { label: nav.privacy, href: localeHref(locale, "privacy") },
    { label: nav.terms, href: localeHref(locale, "terms") },
    { label: nav.support, href: localeHref(locale, "support") }
  ];

  return (
    <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true" aria-label={nav.menu}>
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-x-3 top-3 rounded-2xl border border-subtle bg-bg-card p-5 shadow-card">
        <div className="flex items-center justify-between">
          <span className="font-display text-base font-bold text-text-primary">Luxoria</span>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-subtle text-text-primary"
            aria-label="Close menu"
            onClick={onClose}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <nav className="mt-4 flex flex-col" aria-label="Mobile">
          {links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="border-b border-subtle py-3 text-[15px] font-medium text-text-secondary last:border-0 hover:text-text-primary"
              onClick={onClose}
            >
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </div>
  );
}
